import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { 
  Sparkles, BookOpen, FileText, Wrench, Layout, Image, 
  DollarSign, Share2, Rocket, ChevronRight, ChevronLeft,
  Check, Loader2, Copy, Download, Eye, Edit3, RefreshCw, Crown,
  ImageIcon, Video, Instagram, Play, Palette
} from 'lucide-react';
import { toast } from 'sonner';

const STEPS = [
  { id: 'type', title: 'Product Type', icon: BookOpen },
  { id: 'details', title: 'Product Details', icon: FileText },
  { id: 'content', title: 'AI Content', icon: Sparkles },
  { id: 'visuals', title: 'Visual Assets', icon: Palette },
  { id: 'assets', title: 'Marketplace Assets', icon: Wrench },
  { id: 'pricing', title: 'Pricing', icon: DollarSign },
  { id: 'promo', title: 'Social Promo', icon: Share2 },
  { id: 'publish', title: 'Publish', icon: Rocket },
];

export default function AIProductCreatorPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [productTypes, setProductTypes] = useState([]);
  
  // Form state
  const [selectedType, setSelectedType] = useState(null);
  const [formData, setFormData] = useState({
    topic: '',
    audience: '',
    tone: 'professional',
    goal: ''
  });
  
  // Generated content state
  const [sessionId, setSessionId] = useState(null);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [visualLayout, setVisualLayout] = useState(null);
  const [visualAssets, setVisualAssets] = useState(null);
  const [marketplaceAssets, setMarketplaceAssets] = useState(null);
  const [priceSuggestion, setPriceSuggestion] = useState(null);
  const [socialPromo, setSocialPromo] = useState(null);
  
  // Final publish state
  const [finalPrice, setFinalPrice] = useState('');
  const [priceAccepted, setPriceAccepted] = useState(false);
  const [commissionInfo, setCommissionInfo] = useState({ rate: 0.15, tier: 'starter_creator', tier_info: { name: 'Starter Creator' } });

  useEffect(() => {
    loadProductTypes();
    loadCommissionInfo();
  }, []);

  const loadCommissionInfo = async () => {
    try {
      const res = await api.get('/creator/commission-rate');
      setCommissionInfo(res.data);
    } catch {
      // Use default if not available
    }
  };

  const loadProductTypes = async () => {
    try {
      const res = await api.get('/ai-product-creator/types');
      setProductTypes(res.data.types || []);
    } catch (err) {
      toast.error('Failed to load product types');
    }
  };

  const handleGenerateContent = async () => {
    if (!selectedType || !formData.topic || !formData.audience) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.post('/ai-product-creator/generate', {
        product_type: selectedType.id,
        topic: formData.topic,
        audience: formData.audience,
        tone: formData.tone,
        goal: formData.goal
      });
      
      setSessionId(res.data.session_id);
      setGeneratedContent(res.data.content);
      toast.success('Content generated successfully!');
      setCurrentStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateVisual = async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const res = await api.post('/ai-product-creator/visual-layout', { session_id: sessionId });
      setVisualLayout(res.data.layout);
      toast.success('Visual layout created!');
      setCurrentStep(3);
    } catch (err) {
      toast.error('Failed to generate visual layout');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAllVisuals = async () => {
    if (!sessionId) return;
    setLoading(true);
    toast.info('Generating all visual assets... This may take a minute.');
    
    try {
      const res = await api.post('/ai-product-creator/generate-all-visuals', { session_id: sessionId });
      
      const assets = {
        cover: res.data.cover,
        mockups: res.data.mockups || [],
        preview_pages: res.data.preview_pages,
        social_visuals: res.data.social_visuals || [],
        promo_scripts: res.data.promo_scripts
      };
      
      setVisualAssets(assets);
      
      // Count successful generations
      const counts = {
        cover: assets.cover?.image_url ? 1 : 0,
        mockups: assets.mockups.filter(m => m.url).length,
        previews: assets.preview_pages?.pages?.length || 0,
        socials: assets.social_visuals.filter(v => v.url).length
      };
      
      toast.success(`Generated: ${counts.cover} cover, ${counts.mockups} mockups, ${counts.previews} previews, ${counts.socials} social visuals!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate visual assets');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAssets = async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const res = await api.post('/ai-product-creator/marketplace-assets', { session_id: sessionId });
      setMarketplaceAssets(res.data.assets);
      toast.success('Marketplace assets ready!');
      setCurrentStep(4);
    } catch (err) {
      toast.error('Failed to generate assets');
    } finally {
      setLoading(false);
    }
  };

  const handleGetPriceSuggestion = async () => {
    setLoading(true);
    try {
      const res = await api.post('/ai-product-creator/price-suggestion', {
        product_type: selectedType.id,
        content_depth: 'standard',
        positioning: 'mid-market',
        features_count: generatedContent?.sections?.length || 5
      });
      setPriceSuggestion(res.data);
      setFinalPrice(res.data.suggested_price.toString());
      setCurrentStep(5);
    } catch (err) {
      toast.error('Failed to get price suggestion');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePromo = async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const res = await api.post('/ai-product-creator/social-promo', { session_id: sessionId });
      setSocialPromo(res.data.promo);
      toast.success('Social content created!');
      setCurrentStep(6);
    } catch (err) {
      toast.error('Failed to generate promo content');
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    if (!sessionId || !priceAccepted) {
      toast.error('Please accept the price first');
      return;
    }
    
    setLoading(true);
    try {
      // Determine if this is a wallpaper product
      const isWallpaper = selectedType?.is_wallpaper || ['mirror_wallpaper', 'peaceful_wallpaper', 'luxury_wall_mural', 'digital_wallpaper_pack', 'wallpaper_pack'].includes(selectedType?.id);
      
      const res = await api.post('/ai-product-creator/publish', {
        session_id: sessionId,
        title: marketplaceAssets?.marketplace_title || generatedContent?.title || 'Digital Product',
        description: marketplaceAssets?.long_description || generatedContent?.long_description || generatedContent?.subtitle || '',
        price: parseFloat(finalPrice),
        category: selectedType?.id || 'digital',
        tags: marketplaceAssets?.tags || [],
        cover_concept: visualLayout?.cover_page?.image_suggestion || generatedContent?.cover_concept || ''
      });
      
      toast.success(isWallpaper ? 'Wallpaper published!' : 'Product published to marketplace!');
      
      // Navigate to wallpaper page if it's a wallpaper, otherwise product page
      if (res.data.is_wallpaper) {
        navigate(`/wallpaper/${res.data.product_id}`);
      } else {
        navigate(`/products/${res.data.product_id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Publishing failed');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Product Type Selection
        const regularTypes = productTypes.filter(t => !t.is_wallpaper);
        const wallpaperTypes = productTypes.filter(t => t.is_wallpaper);
        
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="font-serif text-2xl mb-2">Choose Your Product Type</h2>
              <p className="text-sm text-muted-foreground">Select the type of digital product you want to create</p>
            </div>
            
            {/* Regular Digital Products */}
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Digital Products</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {regularTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setSelectedType(type)}
                    className={`p-6 border-2 rounded-xl text-center transition-all hover:border-gold/50 ${
                      selectedType?.id === type.id 
                        ? 'border-gold bg-gold/10' 
                        : 'border-border/40 hover:bg-mist/50'
                    }`}
                    data-testid={`product-type-${type.id}`}
                  >
                    <span className="text-3xl mb-3 block">{type.icon}</span>
                    <h3 className="font-medium text-sm">{type.name}</h3>
                    <p className="text-xs text-muted-foreground mt-1">{type.description}</p>
                    <p className="text-xs text-gold mt-2">From €{type.base_price}</p>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Premium Wallpaper Products */}
            {wallpaperTypes.length > 0 && (
              <div className="pt-4">
                <p className="text-xs uppercase tracking-wider text-amber-700 mb-3 flex items-center gap-2">
                  <ImageIcon size={14} /> Premium Wallpapers
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {wallpaperTypes.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => setSelectedType(type)}
                      className={`p-6 border-2 rounded-xl text-center transition-all ${
                        selectedType?.id === type.id 
                          ? 'border-amber-500 bg-amber-50' 
                          : 'border-amber-200/50 hover:border-amber-300 bg-gradient-to-br from-stone-50 to-amber-50/30'
                      }`}
                      data-testid={`product-type-${type.id}`}
                    >
                      <span className="text-3xl mb-3 block">{type.icon}</span>
                      <h3 className="font-medium text-sm">{type.name}</h3>
                      <p className="text-xs text-stone-500 mt-1">{type.description}</p>
                      <p className="text-xs text-amber-700 mt-2">From €{type.base_price}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {selectedType && (
              <div className="flex justify-end pt-4">
                <Button onClick={() => setCurrentStep(1)} className="rounded-sm" data-testid="next-to-details">
                  Continue <ChevronRight size={16} className="ml-1" />
                </Button>
              </div>
            )}
          </div>
        );
        
      case 1: // Product Details
        return (
          <div className="max-w-2xl mx-auto space-y-6">
            <div className="text-center mb-8">
              <Badge className="mb-2">{selectedType?.icon} {selectedType?.name}</Badge>
              <h2 className="font-serif text-2xl mb-2">Tell Us About Your Product</h2>
              <p className="text-sm text-muted-foreground">Provide details so our AI can create amazing content</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="topic">Topic / Subject *</Label>
                <Input
                  id="topic"
                  value={formData.topic}
                  onChange={(e) => setFormData({...formData, topic: e.target.value})}
                  placeholder="e.g., Building a Successful Morning Routine"
                  className="mt-1"
                  data-testid="input-topic"
                />
              </div>
              
              <div>
                <Label htmlFor="audience">Target Audience *</Label>
                <Input
                  id="audience"
                  value={formData.audience}
                  onChange={(e) => setFormData({...formData, audience: e.target.value})}
                  placeholder="e.g., Busy professionals who want to be more productive"
                  className="mt-1"
                  data-testid="input-audience"
                />
              </div>
              
              <div>
                <Label htmlFor="tone">Tone</Label>
                <select
                  id="tone"
                  value={formData.tone}
                  onChange={(e) => setFormData({...formData, tone: e.target.value})}
                  className="w-full mt-1 p-2 border rounded-md"
                  data-testid="select-tone"
                >
                  <option value="professional">Professional</option>
                  <option value="friendly">Friendly & Conversational</option>
                  <option value="inspirational">Inspirational & Motivating</option>
                  <option value="educational">Educational & Informative</option>
                  <option value="casual">Casual & Relaxed</option>
                </select>
              </div>
              
              <div>
                <Label htmlFor="goal">Product Goal (Optional)</Label>
                <Textarea
                  id="goal"
                  value={formData.goal}
                  onChange={(e) => setFormData({...formData, goal: e.target.value})}
                  placeholder="What transformation should the reader experience?"
                  className="mt-1"
                  rows={3}
                  data-testid="input-goal"
                />
              </div>
            </div>
            
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setCurrentStep(0)} className="rounded-sm">
                <ChevronLeft size={16} className="mr-1" /> Back
              </Button>
              <Button 
                onClick={handleGenerateContent} 
                disabled={loading || !formData.topic || !formData.audience}
                className="rounded-sm bg-gold text-obsidian hover:bg-obsidian hover:text-white"
                data-testid="generate-content-btn"
              >
                {loading ? (
                  <><Loader2 size={16} className="mr-2 animate-spin" /> Generating...</>
                ) : (
                  <><Sparkles size={16} className="mr-2" /> Generate Content</>
                )}
              </Button>
            </div>
          </div>
        );
        
      case 2: // AI Content Preview
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="font-serif text-2xl mb-2">Your AI-Generated Content</h2>
              <p className="text-sm text-muted-foreground">Review and customize your product content</p>
            </div>
            
            {generatedContent && (
              <div className="grid md:grid-cols-2 gap-6">
                {/* Title & Subtitle */}
                <div className="col-span-full bg-gradient-to-r from-gold/10 to-purple-500/10 border border-gold/30 rounded-xl p-6">
                  <h3 className="font-serif text-2xl mb-2">{generatedContent.title}</h3>
                  <p className="text-muted-foreground">{generatedContent.subtitle}</p>
                </div>
                
                {/* Table of Contents */}
                <div className="border border-border/40 rounded-xl p-5">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <BookOpen size={16} /> Table of Contents
                  </h4>
                  <ol className="space-y-2 text-sm">
                    {generatedContent.table_of_contents?.map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-gold font-medium">{i + 1}.</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ol>
                </div>
                
                {/* Bonus Ideas */}
                <div className="border border-border/40 rounded-xl p-5">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Sparkles size={16} /> Bonus Ideas
                  </h4>
                  <ul className="space-y-2 text-sm">
                    {generatedContent.bonus_ideas?.map((bonus, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check size={14} className="text-emerald-500 mt-0.5" />
                        <span>{bonus}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Sections Preview */}
                <div className="col-span-full border border-border/40 rounded-xl p-5">
                  <h4 className="font-medium mb-4 flex items-center gap-2">
                    <FileText size={16} /> Content Sections ({generatedContent.sections?.length || 0})
                  </h4>
                  <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                    {generatedContent.sections?.map((section, i) => (
                      <div key={i} className="bg-mist/50 rounded-lg p-4">
                        <h5 className="font-medium text-sm mb-2">{section.title}</h5>
                        <p className="text-xs text-muted-foreground line-clamp-3">{section.content}</p>
                        {section.key_points?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {section.key_points.map((point, j) => (
                              <Badge key={j} variant="outline" className="text-[10px]">{point}</Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Cover Concept */}
                {generatedContent.cover_concept && (
                  <div className="col-span-full bg-purple-50 border border-purple-200 rounded-xl p-5">
                    <h4 className="font-medium mb-2 flex items-center gap-2 text-purple-700">
                      <Image size={16} /> Cover Design Concept
                    </h4>
                    <p className="text-sm text-purple-600">{generatedContent.cover_concept}</p>
                  </div>
                )}
              </div>
            )}
            
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setCurrentStep(1)} className="rounded-sm">
                <ChevronLeft size={16} className="mr-1" /> Back
              </Button>
              <Button 
                onClick={handleGenerateVisual}
                disabled={loading}
                className="rounded-sm"
                data-testid="generate-visual-btn"
              >
                {loading ? (
                  <><Loader2 size={16} className="mr-2 animate-spin" /> Creating Layout...</>
                ) : (
                  <>Generate Visual Layout <ChevronRight size={16} className="ml-1" /></>
                )}
              </Button>
            </div>
          </div>
        );
        
      case 3: // Visual Assets Generation
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="font-serif text-2xl mb-2">Generate Visual Assets</h2>
              <p className="text-sm text-muted-foreground">AI will create cover, mockups, previews, and social visuals</p>
            </div>
            
            {/* Visual Layout Preview */}
            {visualLayout && (
              <div className="mb-6">
                <div className="bg-gradient-to-br from-obsidian to-gray-800 text-white rounded-xl p-6">
                  <Badge className="mb-3 bg-gold text-obsidian">Cover Preview</Badge>
                  <div className="text-center">
                    <h3 className="font-serif text-2xl mb-2">{generatedContent?.title}</h3>
                    <p className="text-gray-300 text-sm">{generatedContent?.subtitle}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Generated Visual Assets */}
            {visualAssets ? (
              <div className="space-y-6">
                {/* Cover Image */}
                {visualAssets.cover?.image_url && (
                  <div className="border border-emerald-200 bg-emerald-50 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Check size={16} className="text-emerald-500" />
                      <h4 className="font-medium text-emerald-800">Product Cover Generated</h4>
                    </div>
                    <div className="aspect-[2/3] max-w-[200px] rounded-lg overflow-hidden bg-white shadow-lg">
                      <img src={visualAssets.cover.image_url} alt="Cover" className="w-full h-full object-cover" />
                    </div>
                  </div>
                )}
                
                {/* Mockups Grid */}
                {visualAssets.mockups?.length > 0 && (
                  <div className="border border-blue-200 bg-blue-50 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Check size={16} className="text-blue-500" />
                      <h4 className="font-medium text-blue-800">Mockup Images ({visualAssets.mockups.length})</h4>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {visualAssets.mockups.map((mockup, i) => (
                        <div key={i} className="rounded-lg overflow-hidden bg-white shadow">
                          {mockup.url ? (
                            <img src={mockup.url} alt={mockup.type} className="w-full aspect-video object-cover" />
                          ) : (
                            <div className="aspect-video bg-gray-100 flex items-center justify-center text-xs text-gray-400">
                              Pending...
                            </div>
                          )}
                          <p className="text-[10px] text-center p-1 bg-white capitalize">{mockup.type?.replace(/_/g, ' ')}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Preview Pages */}
                {visualAssets.preview_pages?.pages?.length > 0 && (
                  <div className="border border-purple-200 bg-purple-50 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Check size={16} className="text-purple-500" />
                      <h4 className="font-medium text-purple-800">Preview Pages ({visualAssets.preview_pages.pages.length})</h4>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      {visualAssets.preview_pages.pages.map((page, i) => (
                        <div key={i} className="bg-white rounded-lg p-4 shadow">
                          <Badge variant="outline" className="mb-2 text-[10px]">{page.page_type}</Badge>
                          <h5 className="font-medium text-sm mb-1">{page.title}</h5>
                          <p className="text-xs text-muted-foreground line-clamp-3">{page.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Social Visuals */}
                {visualAssets.social_visuals?.length > 0 && (
                  <div className="border border-pink-200 bg-pink-50 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Check size={16} className="text-pink-500" />
                      <h4 className="font-medium text-pink-800">Social Media Visuals ({visualAssets.social_visuals.length})</h4>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {visualAssets.social_visuals.map((visual, i) => (
                        <div key={i} className="rounded-lg overflow-hidden bg-white shadow">
                          {visual.url ? (
                            <img src={visual.url} alt={visual.platform} className="w-full aspect-square object-cover" />
                          ) : (
                            <div className="aspect-square bg-gray-100 flex items-center justify-center text-xs text-gray-400">
                              Pending...
                            </div>
                          )}
                          <p className="text-[10px] text-center p-1 bg-white capitalize">
                            <Instagram size={10} className="inline mr-1" />{visual.platform}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Promo Scripts */}
                {visualAssets.promo_scripts && (
                  <div className="border border-amber-200 bg-amber-50 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Check size={16} className="text-amber-500" />
                      <h4 className="font-medium text-amber-800">Promo Video Scripts</h4>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      {visualAssets.promo_scripts.script_10s && (
                        <div className="bg-white rounded-lg p-3 shadow">
                          <div className="flex items-center gap-1 mb-2">
                            <Play size={12} className="text-amber-600" />
                            <span className="text-xs font-medium">10-Second Script</span>
                          </div>
                          <p className="text-xs text-muted-foreground">{visualAssets.promo_scripts.script_10s.full_script}</p>
                        </div>
                      )}
                      {visualAssets.promo_scripts.script_15s && (
                        <div className="bg-white rounded-lg p-3 shadow">
                          <div className="flex items-center gap-1 mb-2">
                            <Play size={12} className="text-amber-600" />
                            <span className="text-xs font-medium">15-Second Script</span>
                          </div>
                          <p className="text-xs text-muted-foreground">{visualAssets.promo_scripts.script_15s.full_script}</p>
                        </div>
                      )}
                      {visualAssets.promo_scripts.script_30s && (
                        <div className="bg-white rounded-lg p-3 shadow">
                          <div className="flex items-center gap-1 mb-2">
                            <Play size={12} className="text-amber-600" />
                            <span className="text-xs font-medium">30-Second Script</span>
                          </div>
                          <p className="text-xs text-muted-foreground line-clamp-4">{visualAssets.promo_scripts.script_30s.full_script}</p>
                        </div>
                      )}
                    </div>
                    {visualAssets.promo_scripts.cta_variations && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {visualAssets.promo_scripts.cta_variations.slice(0, 5).map((cta, i) => (
                          <Badge key={i} variant="outline" className="text-[10px] bg-white">{cta}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center mx-auto mb-4">
                  <Palette size={24} className="text-purple-500" />
                </div>
                <p className="text-sm text-muted-foreground mb-6">
                  Click below to generate all visual assets automatically
                </p>
                <Button 
                  onClick={handleGenerateAllVisuals}
                  disabled={loading}
                  className="rounded-sm bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                  data-testid="generate-all-visuals-btn"
                >
                  {loading ? (
                    <><Loader2 size={16} className="mr-2 animate-spin" /> Generating All Assets...</>
                  ) : (
                    <><Sparkles size={16} className="mr-2" /> Generate All Visual Assets</>
                  )}
                </Button>
              </div>
            )}
            
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setCurrentStep(2)} className="rounded-sm">
                <ChevronLeft size={16} className="mr-1" /> Back
              </Button>
              <Button 
                onClick={handleGenerateAssets}
                disabled={loading || !visualAssets}
                className="rounded-sm"
                data-testid="generate-assets-btn"
              >
                {loading ? (
                  <><Loader2 size={16} className="mr-2 animate-spin" /> Creating Assets...</>
                ) : (
                  <>Generate Marketplace Assets <ChevronRight size={16} className="ml-1" /></>
                )}
              </Button>
            </div>
          </div>
        );
        
      case 4: // Marketplace Assets
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="font-serif text-2xl mb-2">Marketplace Listing Assets</h2>
              <p className="text-sm text-muted-foreground">Your product listing is almost ready</p>
            </div>
            
            {marketplaceAssets && (
              <div className="space-y-6">
                {/* Title & Descriptions */}
                <div className="border border-border/40 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium">Marketplace Title</h4>
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(marketplaceAssets.marketplace_title)}>
                      <Copy size={14} />
                    </Button>
                  </div>
                  <p className="text-lg font-serif">{marketplaceAssets.marketplace_title}</p>
                  
                  <div className="mt-4 pt-4 border-t">
                    <h5 className="text-sm font-medium mb-2">Short Description</h5>
                    <p className="text-sm text-muted-foreground">{marketplaceAssets.short_description}</p>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t">
                    <h5 className="text-sm font-medium mb-2">Full Description</h5>
                    <p className="text-sm text-muted-foreground whitespace-pre-line">{marketplaceAssets.long_description}</p>
                  </div>
                </div>
                
                {/* Bullets */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="border border-border/40 rounded-xl p-5">
                    <h4 className="font-medium mb-3">Features</h4>
                    <ul className="space-y-2">
                      {marketplaceAssets.feature_bullets?.map((bullet, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <Check size={14} className="text-gold mt-0.5" />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="border border-border/40 rounded-xl p-5">
                    <h4 className="font-medium mb-3">Benefits</h4>
                    <ul className="space-y-2">
                      {marketplaceAssets.benefit_bullets?.map((bullet, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <Sparkles size={14} className="text-purple-500 mt-0.5" />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                {/* Tags */}
                <div className="border border-border/40 rounded-xl p-5">
                  <h4 className="font-medium mb-3">Tags</h4>
                  <div className="flex flex-wrap gap-2">
                    {marketplaceAssets.tags?.map((tag, i) => (
                      <Badge key={i} variant="outline">{tag}</Badge>
                    ))}
                  </div>
                </div>
                
                {/* Launch Statement */}
                <div className="bg-gradient-to-r from-gold/10 to-purple-500/10 border border-gold/30 rounded-xl p-5">
                  <h4 className="font-medium mb-2">Launch Statement</h4>
                  <p className="text-sm">{marketplaceAssets.launch_statement}</p>
                  <p className="text-sm text-gold mt-2">{marketplaceAssets.urgency_hook}</p>
                </div>
              </div>
            )}
            
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setCurrentStep(3)} className="rounded-sm">
                <ChevronLeft size={16} className="mr-1" /> Back
              </Button>
              <Button 
                onClick={handleGetPriceSuggestion}
                disabled={loading}
                className="rounded-sm"
                data-testid="get-price-btn"
              >
                {loading ? (
                  <><Loader2 size={16} className="mr-2 animate-spin" /> Calculating...</>
                ) : (
                  <>Get Price Suggestion <ChevronRight size={16} className="ml-1" /></>
                )}
              </Button>
            </div>
          </div>
        );
        
      case 5: // Pricing
        return (
          <div className="max-w-xl mx-auto space-y-6">
            <div className="text-center mb-8">
              <h2 className="font-serif text-2xl mb-2">Set Your Price</h2>
              <p className="text-sm text-muted-foreground">AI-powered pricing recommendation</p>
            </div>
            
            {priceSuggestion && (
              <div className="space-y-6">
                {/* Suggested Price */}
                <div className="bg-gradient-to-br from-emerald-50 to-green-50 border-2 border-emerald-200 rounded-xl p-8 text-center">
                  <p className="text-sm text-emerald-600 mb-2">Suggested Price</p>
                  <p className="text-5xl font-serif text-emerald-700">€{priceSuggestion.suggested_price}</p>
                  <p className="text-xs text-emerald-500 mt-2">
                    Range: €{priceSuggestion.price_range?.minimum} - €{priceSuggestion.price_range?.maximum}
                  </p>
                </div>
                
                {/* Reasoning */}
                <div className="border border-border/40 rounded-xl p-5">
                  <h4 className="font-medium mb-3">Price Reasoning</h4>
                  <ul className="space-y-2">
                    {priceSuggestion.reasoning?.map((reason, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check size={14} className="text-emerald-500 mt-0.5" />
                        <span>{reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Price Actions */}
                <div className="border border-border/40 rounded-xl p-5 space-y-4">
                  <div className="flex gap-2">
                    <Button
                      onClick={() => { setFinalPrice(priceSuggestion.suggested_price.toString()); setPriceAccepted(true); }}
                      className={`flex-1 rounded-sm ${priceAccepted && finalPrice === priceSuggestion.suggested_price.toString() ? 'bg-emerald-600' : ''}`}
                    >
                      <Check size={16} className="mr-2" /> Accept €{priceSuggestion.suggested_price}
                    </Button>
                    <Button variant="outline" onClick={() => setPriceAccepted(false)} className="rounded-sm">
                      <Edit3 size={16} className="mr-2" /> Custom
                    </Button>
                  </div>
                  
                  {!priceAccepted && (
                    <div>
                      <Label htmlFor="custom-price">Your Price (EUR)</Label>
                      <div className="flex gap-2 mt-1">
                        <Input
                          id="custom-price"
                          type="number"
                          min="0.99"
                          step="0.01"
                          value={finalPrice}
                          onChange={(e) => setFinalPrice(e.target.value)}
                          className="flex-1"
                          data-testid="custom-price-input"
                        />
                        <Button onClick={() => setPriceAccepted(true)} disabled={!finalPrice}>
                          Confirm
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {priceAccepted && (
                    <p className="text-sm text-emerald-600 text-center">
                      ✓ Final price set: €{finalPrice}
                    </p>
                  )}
                </div>
              </div>
            )}
            
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setCurrentStep(4)} className="rounded-sm">
                <ChevronLeft size={16} className="mr-1" /> Back
              </Button>
              <Button 
                onClick={handleGeneratePromo}
                disabled={loading || !priceAccepted}
                className="rounded-sm"
                data-testid="generate-promo-btn"
              >
                {loading ? (
                  <><Loader2 size={16} className="mr-2 animate-spin" /> Creating...</>
                ) : (
                  <>Generate Social Content <ChevronRight size={16} className="ml-1" /></>
                )}
              </Button>
            </div>
          </div>
        );
        
      case 6: // Social Promo
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="font-serif text-2xl mb-2">Social Media Content</h2>
              <p className="text-sm text-muted-foreground">Ready-to-post promotional content</p>
            </div>
            
            {socialPromo && (
              <div className="grid md:grid-cols-2 gap-6">
                {/* Short Captions */}
                <div className="border border-border/40 rounded-xl p-5">
                  <h4 className="font-medium mb-3">Short Captions</h4>
                  <div className="space-y-3">
                    {socialPromo.short_captions?.map((caption, i) => (
                      <div key={i} className="bg-mist/50 p-3 rounded-lg flex items-start justify-between gap-2">
                        <p className="text-sm">{caption}</p>
                        <Button variant="ghost" size="sm" onClick={() => copyToClipboard(caption)}>
                          <Copy size={12} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Premium Captions */}
                <div className="border border-border/40 rounded-xl p-5">
                  <h4 className="font-medium mb-3">Premium Launch Captions</h4>
                  <div className="space-y-3">
                    {socialPromo.premium_launch_captions?.map((caption, i) => (
                      <div key={i} className="bg-purple-50 p-3 rounded-lg flex items-start justify-between gap-2">
                        <p className="text-sm">{caption}</p>
                        <Button variant="ghost" size="sm" onClick={() => copyToClipboard(caption)}>
                          <Copy size={12} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Ad Headlines */}
                <div className="border border-border/40 rounded-xl p-5">
                  <h4 className="font-medium mb-3">Ad Headlines</h4>
                  <div className="space-y-2">
                    {socialPromo.ad_headlines?.map((headline, i) => (
                      <div key={i} className="flex items-center justify-between bg-gold/10 p-2 rounded">
                        <p className="text-sm font-medium">{headline}</p>
                        <Button variant="ghost" size="sm" onClick={() => copyToClipboard(headline)}>
                          <Copy size={12} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* CTAs */}
                <div className="border border-border/40 rounded-xl p-5">
                  <h4 className="font-medium mb-3">Call to Actions</h4>
                  <div className="space-y-2">
                    {socialPromo.cta_lines?.map((cta, i) => (
                      <div key={i} className="flex items-center justify-between bg-emerald-50 p-2 rounded">
                        <p className="text-sm">{cta}</p>
                        <Button variant="ghost" size="sm" onClick={() => copyToClipboard(cta)}>
                          <Copy size={12} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Hashtags */}
                <div className="col-span-full border border-border/40 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium">Hashtags</h4>
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(socialPromo.hashtags?.join(' '))}>
                      <Copy size={12} className="mr-1" /> Copy All
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {socialPromo.hashtags?.map((tag, i) => (
                      <Badge key={i} className="bg-blue-100 text-blue-700 cursor-pointer" onClick={() => copyToClipboard(tag)}>
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setCurrentStep(5)} className="rounded-sm">
                <ChevronLeft size={16} className="mr-1" /> Back
              </Button>
              <Button 
                onClick={() => setCurrentStep(7)}
                className="rounded-sm bg-gold text-obsidian hover:bg-obsidian hover:text-white"
                data-testid="go-to-publish"
              >
                Review & Publish <ChevronRight size={16} className="ml-1" />
              </Button>
            </div>
          </div>
        );
        
      case 7: // Publish
        const commissionRate = commissionInfo?.commission_rate || 0.15;
        const commissionAmount = parseFloat(finalPrice || 0) * commissionRate;
        const sellerPayout = parseFloat(finalPrice || 0) - commissionAmount;
        
        return (
          <div className="max-w-2xl mx-auto space-y-6">
            <div className="text-center mb-8">
              <h2 className="font-serif text-2xl mb-2">Ready to Publish!</h2>
              <p className="text-sm text-muted-foreground">Review your product before going live</p>
            </div>
            
            {/* Final Preview Card */}
            <div className="border-2 border-gold/30 rounded-xl overflow-hidden">
              <div className="bg-gradient-to-r from-gold/20 to-purple-500/20 p-8 text-center">
                <Badge className="mb-4">{selectedType?.icon} {selectedType?.name}</Badge>
                <h3 className="font-serif text-2xl mb-2">{marketplaceAssets?.marketplace_title || generatedContent?.title}</h3>
                <p className="text-muted-foreground">{marketplaceAssets?.short_description || generatedContent?.subtitle}</p>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between py-3 border-b">
                  <span className="text-muted-foreground">Price</span>
                  <span className="text-2xl font-serif text-gold">€{finalPrice}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b">
                  <span className="text-muted-foreground">Category</span>
                  <span>{selectedType?.name}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b">
                  <span className="text-muted-foreground">Sections</span>
                  <span>{generatedContent?.sections?.length || 0} chapters</span>
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="text-muted-foreground">Tags</span>
                  <span>{marketplaceAssets?.tags?.length || 0} tags</span>
                </div>
              </div>
            </div>
            
            {/* Profit Calculator */}
            <div className="bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200 rounded-xl p-5" data-testid="profit-calculator">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
                    <DollarSign size={16} className="text-white" />
                  </div>
                  <p className="font-semibold text-emerald-800">Profit Calculator</p>
                </div>
                <div className="flex items-center gap-2 bg-white/80 px-3 py-1.5 rounded-full border border-emerald-200">
                  <Crown size={12} className="text-gold" />
                  <span className="text-xs font-medium">{commissionInfo?.tier_info?.name || 'Starter Creator'}</span>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center py-2 border-b border-emerald-200/50">
                  <span className="text-emerald-700">Customer pays:</span>
                  <span className="font-semibold text-lg">€{parseFloat(finalPrice || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span className="text-emerald-600 flex items-center gap-1">
                    Platform commission:
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700">
                      {(commissionRate * 100).toFixed(0)}%
                    </span>
                  </span>
                  <span className="text-red-500">-€{commissionAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-t-2 border-emerald-300 mt-2">
                  <span className="font-bold text-emerald-800">Your Payout per Sale:</span>
                  <span className="font-bold text-2xl text-emerald-600">€{sellerPayout.toFixed(2)}</span>
                </div>
              </div>
            </div>
            
            {/* Publish Actions */}
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setCurrentStep(6)} className="rounded-sm">
                <ChevronLeft size={16} className="mr-1" /> Back
              </Button>
              <Button 
                onClick={handlePublish}
                disabled={loading}
                className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white px-8"
                data-testid="publish-product-btn"
              >
                {loading ? (
                  <><Loader2 size={16} className="mr-2 animate-spin" /> Publishing...</>
                ) : (
                  <><Rocket size={16} className="mr-2" /> Publish to Marketplace</>
                )}
              </Button>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="ai-product-creator-page">
      {/* Header */}
      <div className="border-b border-border/40 bg-white sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold to-purple-500 flex items-center justify-center">
                <Sparkles size={20} className="text-white" />
              </div>
              <div>
                <h1 className="font-serif text-lg">AI Product Creator</h1>
                <p className="text-xs text-muted-foreground">Create & publish digital products with AI</p>
              </div>
            </div>
            
            {sessionId && (
              <Badge variant="outline" className="text-xs">
                Session: {sessionId.slice(0, 8)}...
              </Badge>
            )}
          </div>
        </div>
      </div>
      
      {/* Progress Steps */}
      <div className="border-b border-border/40 bg-mist/50">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between overflow-x-auto">
            {STEPS.map((step, i) => {
              const StepIcon = step.icon;
              const isActive = i === currentStep;
              const isComplete = i < currentStep;
              
              return (
                <div 
                  key={step.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-full transition-all ${
                    isActive ? 'bg-gold text-obsidian' : 
                    isComplete ? 'bg-emerald-100 text-emerald-700' : 
                    'text-muted-foreground'
                  }`}
                >
                  {isComplete ? (
                    <Check size={16} />
                  ) : (
                    <StepIcon size={16} />
                  )}
                  <span className="text-xs font-medium whitespace-nowrap hidden md:inline">{step.title}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {renderStepContent()}
      </div>
    </div>
  );
}
